/**
 * LeadSpace.tsx — hero-seksjon for forsiden (ibm.com «leadspace»)
 *
 * Venstre kolonne: gradient-overskrift (h1), lead-in og CTA-gruppe.
 * Høyre kolonne: skjermbilde av dashbordet i ramme. Lys/mørk variant byttes
 * med CSS (html.cds--g100) slik at riktig bilde vises fra første tegning.
 *
 * Grid: sm=4 / md=8 / lg=16 kolonner (kolonner stables under lg).
 * A11y: én <h1>, CTA-gruppe som liste, alt-tekst på bildet.
 */

import Image from 'next/image';
import type { ReactNode } from 'react';
import type { LandingHero } from '@/types/landing';
import LandingButton from './LandingButton';
import styles from './lead-space.module.css';

type LeadSpaceHero = Pick<LandingHero, 'heading' | 'subheading' | 'primaryCta' | 'secondaryCta'> &
  Partial<Pick<LandingHero, 'imageLightSrc' | 'imageDarkSrc' | 'imageAlt'>>;

interface LeadSpaceProps {
  hero: LeadSpaceHero;
  /** Egen media (f.eks. piktogram) i stedet for skjermbilde */
  media?: ReactNode;
}

export default function LeadSpace({ hero, media }: LeadSpaceProps) {
  return (
    <section className={styles.leadSpace} aria-labelledby="leadspace-heading">
      <div className={styles.grid}>
        <div className={styles.contentColumn}>
          <h1 id="leadspace-heading" className={styles.heading}>
            {hero.heading}
          </h1>
          <p className={styles.subheading}>{hero.subheading}</p>

          <ul className={styles.ctaGroup} role="list">
            <li>
              <LandingButton link={hero.primaryCta} kind="primary" />
            </li>
            <li>
              <LandingButton link={hero.secondaryCta} kind="tertiary" />
            </li>
          </ul>
        </div>

        <div className={styles.mediaColumn}>
          <div className={styles.frame}>
            {media}
            {hero.imageLightSrc && hero.imageDarkSrc && (
              <>
            <Image
              src={hero.imageLightSrc}
              alt={hero.imageAlt ?? ''}
              width={1440}
              height={900}
              priority
              sizes="(min-width: 1056px) 56vw, 100vw"
              className={`${styles.media} ${styles.mediaLight}`}
            />
            <Image
              src={hero.imageDarkSrc}
              alt=""
              aria-hidden
              width={1440}
              height={900}
              loading="eager"
              sizes="(min-width: 1056px) 56vw, 100vw"
              className={`${styles.media} ${styles.mediaDark}`}
            />
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
